import { ACCESS_RESOURCES, type ResourceId, pickPermissionKey } from '@/config/access-control'
import type { Permissions } from '@/rbac/grants'
import {
	type ViewGrantScope,
	getViewGrantScope,
	hasAnyViewGrant,
	viewGrantAllowsModule,
} from '@/utils/access-control/grantScope'

export type RouteAccessScenario =
	| 'route_denied'
	| 'empty_with_grant'
	| 'empty_scoped_grant'
	| 'api_forbidden'
	| 'api_forbidden_with_grant'
	| 'api_forbidden_ungated'

export type RouteAccessDeniedReason = 'no-permission' | 'not-owner' | 'not-admin'

const RESOURCE_LABELS: Record<ResourceId, string> = {
	projects: 'Projects',
	devices: 'Devices',
	dashboards: 'Dashboards',
	remote_commands: 'Remote Commands',
	custom_commands: 'Action Types',
	streams: 'Streams',
	metadata_keys: 'Metadata Keys',
	device_state: 'Device State',
	users: 'Users',
	roles: 'Roles',
	api_keys: 'API Keys',
	dbc_parsers: 'DBC Parsers',
	exporters: 'Exporters',
	subscriber_groups: 'Subscriber Groups',
	session_rules: 'Session Rules',
	firmware: 'Firmware',
	files: 'Files',
	json_config: 'JSON Config',
	geofence_config: 'Geofence Config',
	notifications: 'Notifications',
	project_settings: 'Project Settings',
	branding: 'Branding',
	ota_campaigns: 'OTA Campaigns',
	processors: 'Processors',
	anomalies: 'Anomalies',
	pull_based_actions: 'Pull-based Actions',
	dev_mode: 'Developer Mode',
	pushers: 'Pushers',
}

const CONTACT_ADMIN_SUFFIX = ' Ask an administrator to grant access.'
// For a resource with no grantable permission at all (see ACCESS_RESOURCES entries with no permission
// fields, e.g. `anomalies`, `processors`): no administrator action can fix a 403 here, so the generic
// "ask an administrator" advice would be a dead end.
const CONTACT_SUPPORT_SUFFIX = ' Contact support for help.'

export function getResourceLabel(resource: ResourceId): string {
	return RESOURCE_LABELS[resource] ?? resource
}

/** @deprecated Use getResourceLabel */
export const getResourceModuleLabel = getResourceLabel

export function getRouteAccessDeniedMessage(
	resource: ResourceId,
	reason: RouteAccessDeniedReason = 'no-permission',
): string {
	const label = getResourceLabel(resource)
	if (reason === 'not-owner') {
		return `Only project owners can manage ${label}.`
	}
	if (reason === 'not-admin') {
		return `Only project administrators can access ${label}.`
	}
	return `You do not have permission to view ${label}.${CONTACT_ADMIN_SUFFIX}`
}

export function getEmptyListAccessMessage(resource: ResourceId, scope: ViewGrantScope): string {
	const label = getResourceLabel(resource)
	if (scope === 'all') {
		return `No ${label} in this project yet. If you expected some, ask an administrator.`
	}
	return `Your role covers only part of ${label}, and nothing here matches it.${CONTACT_ADMIN_SUFFIX}`
}

export function getApiForbiddenMessage(resource: ResourceId, hasMatchingGrant: boolean): string {
	const label = getResourceLabel(resource)
	if (hasMatchingGrant) {
		return `Your role covers part of ${label}, but the server denied this request.${CONTACT_ADMIN_SUFFIX}`
	}
	return `You do not have permission to access ${label}.${CONTACT_ADMIN_SUFFIX}`
}

/** The server denied a resource the frontend never gates by permission in the first place — there is no
 *  role setting for an administrator to grant, so this points at support instead. */
export function getApiForbiddenMessageForUngatedResource(resource: ResourceId): string {
	const label = getResourceLabel(resource)
	return `You do not have permission to access ${label}.${CONTACT_SUPPORT_SUFFIX}`
}

export function resolveRouteAccessMessage(input: {
	resource: ResourceId
	scenario: RouteAccessScenario
	reason?: RouteAccessDeniedReason
	viewGrantScope?: ViewGrantScope
}): string {
	const { resource, scenario, reason = 'no-permission', viewGrantScope } = input
	switch (scenario) {
		case 'route_denied':
			return getRouteAccessDeniedMessage(resource, reason)
		case 'empty_with_grant':
			return getEmptyListAccessMessage(resource, viewGrantScope ?? 'all')
		case 'empty_scoped_grant':
			return getEmptyListAccessMessage(resource, 'partial')
		case 'api_forbidden':
			return getApiForbiddenMessage(resource, false)
		case 'api_forbidden_with_grant':
			return getApiForbiddenMessage(resource, true)
		case 'api_forbidden_ungated':
			return getApiForbiddenMessageForUngatedResource(resource)
	}
}

function grantForPermissionKey(permissions: Permissions | undefined, permission?: keyof Permissions): unknown {
	return permission ? permissions?.[permission] : undefined
}

export function getViewGrantScopeForResource(
	permissions: Permissions | undefined,
	_resource: ResourceId,
	viewPermission?: keyof Permissions,
	complexPermissionKey?: keyof Permissions,
): ViewGrantScope {
	const viewGrant = grantForPermissionKey(permissions, viewPermission)
	if (viewGrantAllowsModule(viewGrant)) {
		return getViewGrantScope(viewGrant)
	}
	return getViewGrantScope(grantForPermissionKey(permissions, complexPermissionKey))
}

export function getPermissionKeysForResource(
	_resource: ResourceId,
	viewPermission?: keyof Permissions,
	complexPermissionKey?: keyof Permissions,
): (keyof Permissions)[] {
	const keys: (keyof Permissions)[] = []
	if (viewPermission) {
		keys.push(viewPermission)
	}
	if (complexPermissionKey && complexPermissionKey !== viewPermission) {
		keys.push(complexPermissionKey)
	}
	return keys
}

export function classifyApiAccessFailure(input: {
	status: number
	permissions?: Permissions
	resource?: ResourceId
	viewPermission?: keyof Permissions
	complexPermissionKey?: keyof Permissions
}): RouteAccessScenario | null {
	const { status, permissions, resource, viewPermission, complexPermissionKey } = input
	if (status !== 403 || !resource) {
		return null
	}
	const keys = getPermissionKeysForResource(resource, viewPermission, complexPermissionKey)
	if (keys.length === 0) {
		return 'api_forbidden_ungated'
	}
	const hasGrant = permissions ? hasAnyViewGrant(permissions, keys) : false
	return hasGrant ? 'api_forbidden_with_grant' : 'api_forbidden'
}

export function getApiAccessFailureMessage(input: {
	status: number
	permissions?: Permissions
	resource: ResourceId
	viewPermission?: keyof Permissions
	complexPermissionKey?: keyof Permissions
}): string | null {
	const scenario = classifyApiAccessFailure(input)
	if (!scenario) {
		return null
	}
	return resolveRouteAccessMessage({ resource: input.resource, scenario })
}

export function getEmptyResponseAccessMessage(input: {
	resource: ResourceId
	permissions?: Permissions
	viewPermission?: keyof Permissions
	complexPermissionKey?: keyof Permissions
	isEmpty: boolean
}): string | null {
	if (!input.isEmpty || !input.permissions) {
		return null
	}
	const scope = getViewGrantScopeForResource(
		input.permissions,
		input.resource,
		input.viewPermission,
		input.complexPermissionKey,
	)
	// A full view grant has nothing to add: the reader can see everything there is, so the list being
	// empty means it is empty, which the page's own copy already says. Only a partial grant explains
	// something the reader cannot work out, namely that their role hides part of this list. A denial
	// never reaches here; it renders the access restricted panel instead.
	//
	// The scope decides this directly. It used to go through `viewGrantAllowsModule`, which expects a
	// grant rather than a scope: `'all'` read as an active grant and `'partial'` read as no grant, so
	// the message appeared for exactly the roles it had nothing to tell, admins included, and never
	// for the scoped role it was written for.
	if (scope !== 'partial') {
		return null
	}
	return resolveRouteAccessMessage({
		resource: input.resource,
		scenario: 'empty_scoped_grant',
		viewGrantScope: scope,
	})
}

function viewPermissionKeysForResource(resource: ResourceId): {
	viewPermission?: keyof Permissions
	complexPermissionKey?: keyof Permissions
} {
	const cfg = ACCESS_RESOURCES[resource]
	return {
		viewPermission: pickPermissionKey(cfg, 'view'),
		complexPermissionKey: 'complexPermissionKey' in cfg ? cfg.complexPermissionKey : undefined,
	}
}

export function getEmptyResponseAccessMessageForResource(
	resource: ResourceId,
	permissions: Permissions | undefined,
	isEmpty: boolean,
): string | null {
	const keys = viewPermissionKeysForResource(resource)
	return getEmptyResponseAccessMessage({
		resource,
		permissions,
		...keys,
		isEmpty,
	})
}

export function getApiAccessFailureMessageForResource(
	status: number,
	resource: ResourceId,
	permissions: Permissions | undefined,
): string | null {
	const keys = viewPermissionKeysForResource(resource)
	return getApiAccessFailureMessage({
		status,
		resource,
		permissions,
		...keys,
	})
}
